import { z } from 'zod';
import { optionalUuidString, parseStrictDate } from '../zod-helpers/index.js';
import { MAX_TRANSFER_ORDER_LINES_PER_CREATE, MAX_TRANSFER_ORDER_LINES_PER_CREATE_MESSAGE, TRANSFER_ORDER_CONSTRAINTS, } from './constraints.js';
import { refineTransferOrderWarehousesDiffer } from './create-input.js';
import { parseTransferOrderQuantity } from './item-quantity.js';
const strictDateSchema = z.string().transform((value, context) => parseStrictDate(value, context));
function isInvalidDateString(value) {
    return !strictDateSchema.safeParse(value).success;
}
export const transferOrderPartLineFormFieldsSchema = z.object({
    partId: optionalUuidString('Invalid part selection.'),
    partNum: z.string(),
    name: z.string().nullable(),
    categoryName: z.string().nullable().optional(),
    uom: z.object({ id: z.string(), code: z.string(), name: z.string() }).nullable().optional(),
    qty: z.string().trim(),
});
export const transferOrderCreateFormFieldsSchema = z.object({
    brandId: optionalUuidString('Invalid brand selection.'),
    fromLocationGroupId: optionalUuidString(`Invalid ${TRANSFER_ORDER_CONSTRAINTS.fromLocationGroup.label.toLowerCase()} selection.`),
    shipToLocationGroupId: optionalUuidString(`Invalid ${TRANSFER_ORDER_CONSTRAINTS.shipToLocationGroup.label.toLowerCase()} selection.`),
    carrierId: optionalUuidString('Invalid carrier selection.'),
    carrierServiceId: optionalUuidString('Invalid carrier service selection.'),
    dateScheduled: z.string().trim(),
    splitOnShortPick: z.boolean(),
    ownerIsFrom: z.boolean(),
    note: z
        .string()
        .trim()
        .max(TRANSFER_ORDER_CONSTRAINTS.note.maxLength, `${TRANSFER_ORDER_CONSTRAINTS.note.label} must be ${TRANSFER_ORDER_CONSTRAINTS.note.maxLength} characters or fewer.`),
    lines: z
        .array(transferOrderPartLineFormFieldsSchema)
        .max(MAX_TRANSFER_ORDER_LINES_PER_CREATE, MAX_TRANSFER_ORDER_LINES_PER_CREATE_MESSAGE),
});
export function createTransferOrderCreateFormSchema() {
    return transferOrderCreateFormFieldsSchema.superRefine((values, context) => {
        if (!values.brandId) {
            context.addIssue({
                code: 'custom',
                message: `${TRANSFER_ORDER_CONSTRAINTS.brand.label} is required.`,
                path: ['brandId'],
            });
        }
        if (!values.fromLocationGroupId) {
            context.addIssue({
                code: 'custom',
                message: `${TRANSFER_ORDER_CONSTRAINTS.fromLocationGroup.label} is required.`,
                path: ['fromLocationGroupId'],
            });
        }
        if (!values.shipToLocationGroupId) {
            context.addIssue({
                code: 'custom',
                message: `${TRANSFER_ORDER_CONSTRAINTS.shipToLocationGroup.label} is required.`,
                path: ['shipToLocationGroupId'],
            });
        }
        refineTransferOrderWarehousesDiffer(values, context);
        if (!values.carrierId) {
            context.addIssue({
                code: 'custom',
                message: `${TRANSFER_ORDER_CONSTRAINTS.carrier.label} is required.`,
                path: ['carrierId'],
            });
        }
        if (values.dateScheduled.trim() && isInvalidDateString(values.dateScheduled)) {
            context.addIssue({
                code: 'custom',
                message: `${TRANSFER_ORDER_CONSTRAINTS.dateScheduled.label} must be a valid date.`,
                path: ['dateScheduled'],
            });
        }
        if (values.lines.length === 0) {
            context.addIssue({
                code: 'custom',
                message: 'Select at least one item to transfer.',
                path: ['lines'],
            });
            return;
        }
        values.lines.forEach((line, lineIndex) => {
            if (!line.partId) {
                context.addIssue({
                    code: 'custom',
                    message: `${TRANSFER_ORDER_CONSTRAINTS.part.label} is required.`,
                    path: ['lines', lineIndex, 'partId'],
                });
            }
            if (parseTransferOrderQuantity(line.qty) === undefined) {
                context.addIssue({
                    code: 'custom',
                    message: `${TRANSFER_ORDER_CONSTRAINTS.quantity.label} must be a valid number of at least ${TRANSFER_ORDER_CONSTRAINTS.quantity.minText} and ${TRANSFER_ORDER_CONSTRAINTS.quantity.maxText} or less.`,
                    path: ['lines', lineIndex, 'qty'],
                });
            }
        });
    });
}
//# sourceMappingURL=create-form.js.map